import { useCallback, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { appendDefaultOption } from "helpers/commonHelpers";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import DatePicker from "components/atoms/DatePicker/DatePicker";
import InputField from "components/atoms/InputField/InputField";
import Radio from "components/atoms/Radio/Radio";
import testIds from "constants/testIds";
import usePermissions from "hooks/usePermissions";
import {
  calculateGoodItemsTotalWeight,
  getBaseWeightUnit,
  getChargeableWeight,
  getDimensionalWeight,
} from "helpers/quotationFormHelper";
import useGetMultiDrop from "../../Schedule/hooks/useGetMultiDrop";
import CheckboxWithLabel from "./CheckboxWithLabel";
import {
  dangerousGoodsOptions,
  unitMeasurementCmOrInch,
  unitMeasurementKgOrLbs,
  temperatureControlledOptions,
} from "../PackingListDefaultOptions";

const DANGEROUS_GOOD_ADDITIONAL_FIELDS = ["dangerous_good_value", "un_number"] as const;

/**
 * Calculate the CBM of an item
 * @param item - The packing list item
 * @returns the CBM of the item
 */
const calculateItemCBM = (item: any) => {
  // Convert dimensions to meters
  const dimensionMultiplier = item.m_unit === "inch" ? 0.0254 : 0.01; // 1 inch = 0.0254 meters, 1 cm = 0.01 meters
  const length = parseFloat(item.length) * dimensionMultiplier;
  const width = parseFloat(item.width) * dimensionMultiplier;
  const height = parseFloat(item.height) * dimensionMultiplier;
  const units = parseFloat(item.units);

  // If length, width, and height are provided, calculate CBM
  if (length && width && height) return length * width * height * units;
  // If cbm_scope is "piece," calculate CBM per unit
  if (item.cbm_scope === "piece") return parseFloat(item.l_w_h_cbm_piece) * parseFloat(item.units);
  // If cbm_scope is "total," use the provided total CBM value
  if (item.cbm_scope === "total") return parseFloat(item.l_w_h_cbm_total);
  return 0; // Handle other cases as needed
};

/**
 * Formats a number to remove trailing zeros
 * @param value - The number to format
 * @returns a formatted number
 */
const formatNumber = (value: number) => {
  if (value == null || Number.isNaN(Number(value))) return "0.00";
  const numericValue = Number(value);
  if (!numericValue) return "0.00";
  const formattedNumber = numericValue.toFixed(2);
  return formattedNumber.endsWith("00") ? numericValue.toFixed(0) : formattedNumber;
};

function PackingListGlobalConfiguration({ packingListSettings, state }: any) {
  const intl = useIntl();
  const { getValues, setValue } = useFormContext();
  const { permissions } = usePermissions();
  const { company_shipper_settings } = JSON.parse(permissions || "{}");
  const { isMultiDropQuoteRequest } = useGetMultiDrop();

  // These watch values are used to show/hide nested fields that are shown conditionally
  const [
    cratingRequired,
    temperatureControlRequired,
    temperatureControlValue,
    temperatureControlCustomValue,
    isDangerousGood,
    packingList,
  ] = useWatch({
    name: [
      "packing.global_configuration.crating_required",
      "packing.global_configuration.temperature_control_required",
      "packing.global_configuration.temperature_control_value",
      "packing.global_configuration.temperature_controlled_custom_value",
      "packing.global_configuration.is_dangerous_good",
      "packing_list",
    ],
  });

  // If temperature control custom value changes, apply it to all items in the packing list
  useEffect(() => {
    // If temperature control is not set to Other, return
    if (getValues("packing.global_configuration.temperature_control_value") !== "6") return;

    // Apply the custom value to all items in the packing list
    getValues("packing_list").forEach((item: any, index: number) => {
      setValue(`packing_list.${index}.temperature_controlled_custom_value`, temperatureControlCustomValue);
    });
  }, [temperatureControlCustomValue]);

  // Calculate the total weight and CBM of all items in the packing list
  const totalWeightAndCBM = useCallback(
    () => {
      if (!Array.isArray(packingList)) return undefined;

      const items = packingList.filter(Boolean);
      const targetUnit = getBaseWeightUnit(
        getValues("packing.global_configuration.w_unit") || items[0]?.w_unit,
      );
      const { weight } = calculateGoodItemsTotalWeight(items, targetUnit);

      const nonContainerItems = items.filter(
        (curr: any) => !(typeof curr.handling_unit === "string" && curr.handling_unit.toLowerCase().includes("container")),
      );
      const { weight: weightExcludingContainer } = calculateGoodItemsTotalWeight(nonContainerItems, targetUnit);

      return items.reduce(
        (acc: any, curr: any) => {
          acc.cbm += calculateItemCBM(curr);
          if (!(typeof curr.handling_unit === "string" && curr.handling_unit.toLowerCase().includes("container"))) {
            acc.cbmExcludingContainer += calculateItemCBM(curr);
          }
          return acc;
        },
        {
          cbm: 0,
          weight,
          cbmExcludingContainer: 0,
          weightExcludingContainer,
          unit: targetUnit,
        },
      );
    },
    [packingList, getValues],
  );
  /**
   * Checks if any of the freight modes selected is air
   * @returns {boolean} true if any of the freight modes selected is air
   */
  const isAirFreightMode = () => {
    const selectedFreightModes = getValues("invited_modes");
    if (!selectedFreightModes) return false;
    // Combine standard and custom freight modes
    const allFreightModes = [
      ...Object.keys(selectedFreightModes),
      ...(getValues("invited_modes.custom") || [])
        .filter((item: any) => item.value)
        .map((item: any) => `custom_${item.key}`),
    ];

    // Check if any of the selected modes with value: true are of type "air"
    return allFreightModes.some((key) => {
      const mode = state.freightModes?.available_modes?.[key];
      if (!mode) return false;

      // Extract the number from the key (e.g., "custom_1" -> 1)
      const customKey = key.match(/custom_(\d+)/);

      let isSelected = false;
      if (customKey) {
        isSelected = selectedFreightModes.custom.some(
          (item: any) => item.key === Number(customKey[1]) && item.value === true,
        );
      } else isSelected = selectedFreightModes[key];

      return mode && mode.type === "air" && isSelected;
    });
  };

  // Update the unit of measurement for all items in the packing list
  const updatePackingListUnit = (value: any, unitType: "m_unit" | "w_unit") => {
    const packingList = getValues("packing_list") || [];
    packingList.forEach((item: any, index: number) => {
      if (item[unitType] === value) return;
      const itemIndex = typeof item.index === "number" ? item.index : index;
      setValue(`packing_list.${itemIndex}.${unitType}`, value);
    });
  };

  // Update the value of a property for all items in the packing list
  const updatePackingListProperties = (
    isChecked: any,
    property:
      | "stackable"
      | "hazardous"
      | "crating_required"
      | "temperature_control_required"
      | "is_dangerous_good"
      | "handling_required",
  ) => {
    const propertyMappings: any = {
      crating_required: "crating_required_by",
      temperature_control_required: "temperature_control_value",
    };

    const packingList = getValues("packing_list");

    // Check if there are differences in the property value among items in the packing list
    const hasDifferences = !packingList.every((item: any) => item[property] === packingList[0][property]);

    // Function to update the value of a property and its additional field
    const updateItemProperty = (item: any) => {
      setValue(`packing_list.${item.index}.${property}`, isChecked);
      if (property === "is_dangerous_good") {
        DANGEROUS_GOOD_ADDITIONAL_FIELDS.forEach((field) => {
          if (!isChecked) {
            setValue(`packing_list.${item.index}.${field}`, field === "un_number" ? "" : null);
          } else {
            setValue(
              `packing_list.${item.index}.${field}`,
              getValues(`packing.global_configuration.${field}`) ?? (field === "un_number" ? "" : null),
            );
          }
        });
      } else if (propertyMappings[property]) {
        if (!isChecked) {
          setValue(`packing_list.${item.index}.${propertyMappings[property]}`, null);
        } else {
          const additionalFieldGlobalConfigValue = getValues(`packing.global_configuration.${propertyMappings[property]}`);
          setValue(`packing_list.${item.index}.${propertyMappings[property]}`, additionalFieldGlobalConfigValue);
        }
      }
    };

    if (hasDifferences) {
      // Show a dialog popup to inform the user about applying changes to all packing list items
      if (window.confirm("Changes will be applied to all items in the packing list. Continue?"))
        // User confirmed the changes, update the values for all items
        packingList.forEach(updateItemProperty);
    } else {
      // No differences in the property value, proceed with updating values for all items
      packingList.forEach(updateItemProperty);
    }
  };

  // Update the value of a property for all items in the packing list
  const updatePackingListAdditionalProperty = (
    property:
      | "crating_required_by"
      | "temperature_control_value"
      | "dangerous_good_value"
      | "un_number"
      | "temperature_control_custom_value",
    value: any,
  ) => {
    const propertyMappings: any = {
      crating_required_by: "crating_required",
      temperature_control_value: "temperature_control_required",
      temperature_control_custom_value: "temperature_control_required",
      dangerous_good_value: "is_dangerous_good",
      un_number: "is_dangerous_good",
    };

    const packingList = getValues("packing_list");

    packingList.forEach((item: any) => {
      const isPropertyFieldEnabled = getValues(`packing_list.${item.index}.${propertyMappings[property]}`);
      // If the item has the property field enabled, update the value
      if (isPropertyFieldEnabled) setValue(`packing_list.${item.index}.${property}`, value);
    });
  };

  return (
    <div className="d-flex">
      <div className="d-flex flex-column">
        <div className="flex-row mt-2">
          <div className="w-200 font-b3 mr-3">
            <span className="grey-800">
              <FormattedMessage id="general.totalCBM" defaultMessage="Total CBM" />:
            </span>
            <span data-testid={testIds.REQUEST_FORM_PACKING_LIST_TOTAL_CBM}>
              <span className="ml-1 font-weight-bold" data-testid="total-cbm">
                {formatNumber(totalWeightAndCBM()?.cbm)} m<sup>3</sup>
              </span>
            </span>
          </div>
        </div>
        <div className="flex-row">
          <div className="w-200 d-flex font-b3 align-items-center">
            <span className="grey-800">
              <FormattedMessage id="new-request.packing-list.totalWeight" defaultMessage="Total weight:" />
            </span>
            <span data-testid={testIds.REQUEST_FORM_PACKING_LIST_TOTAL_WEIGHT}>
              <span className="ml-1 font-weight-bold" data-testid="total-weight">
                {formatNumber(totalWeightAndCBM()?.weight)} {totalWeightAndCBM()?.unit}
              </span>
            </span>
          </div>
        </div>
        {isAirFreightMode() &&
          // If the total CBM or weight excluding containers is greater than 0, show the total chargeable weight
          getChargeableWeight(
            totalWeightAndCBM()?.weightExcludingContainer,
            getDimensionalWeight(packingList, totalWeightAndCBM()?.unit),
          ) > 0 && (
            <div className="flex-row">
              <div className="w-200 d-flex font-b3 align-items-center">
                <span className="grey-800">Total chargeable weight:</span>
                <span className="ml-1 font-weight-bold">
                  {formatNumber(
                    getChargeableWeight(
                      totalWeightAndCBM()?.weightExcludingContainer,
                      getDimensionalWeight(packingList, totalWeightAndCBM()?.unit),
                    ),
                  )}{" "}
                  {totalWeightAndCBM()?.unit}
                </span>
              </div>
            </div>
          )}
      </div>
      <div className="d-flex flex-column">
        <div className="flex-row mt-1">
          {/* Unit of measurement (cm/inch) */}
          <div className="w-90 dropdown-div">
            <Dropdown
              useHookForm
              shouldUnregister
              name="packing.global_configuration.m_unit"
              dataTestid="packing.global_configuration.m_unit"
              className="schedule-dropdown"
              options={unitMeasurementCmOrInch(intl)}
              onChange={({ value }: any) => updatePackingListUnit(value, "m_unit")}
              defaultValue={
                unitMeasurementCmOrInch(intl)?.find((unit: any) => unit.value === packingListSettings.dimension_uom_default)
                  ?.value
              }
            />
          </div>
          {/* Unit of measurement (kg/lbs) */}
          <div className="w-90 dropdown-div mb-2">
            <Dropdown
              useHookForm
              shouldUnregister
              name="packing.global_configuration.w_unit"
              dataTestid="packing.global_configuration.w_unit"
              className="schedule-dropdown"
              options={unitMeasurementKgOrLbs(intl)}
              onChange={({ value }: any) => updatePackingListUnit(value, "w_unit")}
              defaultValue={
                unitMeasurementKgOrLbs(intl)?.find((unit: any) => unit.value === packingListSettings.weight_uom_default)
                  ?.value
              }
            />
          </div>
          {/* Goods list estimation */}
          {company_shipper_settings.goods_list_estimation && (
            <div className="col-right">
              <div className="flex-row mb-4">
                <div>
                  <div className="required label font-b4">
                    <FormattedMessage id="new-request.organisational-ThisGoodList" defaultMessage="Goods list is" />
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="w-192">
                      <Radio
                        className="border-0"
                        name="organizational.goods_confirmation_type"
                        options={[
                          {
                            value: "Estimated",
                            label: intl.formatMessage({
                              id: "new-request.organisational-Estimated",
                              defaultMessage: "Estimated",
                            }),
                          },
                          {
                            value: "Finalized",
                            label: intl.formatMessage({
                              id: "new-request.organisational-Finalized",
                              defaultMessage: "Finalized",
                            }),
                          },
                        ]}
                        customOption
                        customColClass="pl-0 pb-3"
                        defaultChecked={getValues("organizational.goods_confirmation_type")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex-row flex-wrap mt-4">
          {/* Stackable checkbox */}
          <CheckboxWithLabel
            label={intl.formatMessage({
              id: "new-request.packing-list.stackable",
              defaultMessage: "Stackable",
            })}
            name="packing.global_configuration.stackable"
            onChange={(e: any) => updatePackingListProperties(e.target.checked, "stackable")}
            showCheckbox
          />
          {/* Hazardous checkbox */}
          <CheckboxWithLabel
            label={intl.formatMessage({
              id: "new-request.packing-list.hazardous",
              defaultMessage: "Hazardous",
            })}
            name="packing.global_configuration.hazardous"
            onChange={(e: any) => updatePackingListProperties(e.target.checked, "hazardous")}
            showCheckbox={packingListSettings?.hazardous_goods_enabled}
          />
          {/* Crating required checkbox */}
          <div className="d-flex flex-column">
            <CheckboxWithLabel
              label={intl.formatMessage({
                id: "new-request.packing-list.cratingRequiredBy",
                defaultMessage: "Crating required by",
              })}
              name="packing.global_configuration.crating_required"
              onChange={(e: any) => updatePackingListProperties(e.target.checked, "crating_required")}
              showCheckbox={packingListSettings?.crating_required_enabled}
            />
            {cratingRequired && (
              <DatePicker
                name="packing.global_configuration.crating_required_by"
                onDateSelect={() =>
                  updatePackingListAdditionalProperty(
                    "crating_required_by",
                    getValues("packing.global_configuration.crating_required_by"),
                  )
                }
                required
              />
            )}
          </div>
          {/* Temperature controlled checkbox */}
          <div className="d-flex flex-column">
            <CheckboxWithLabel
              label={intl.formatMessage({
                id: "new-request.packing-list.temperatureControlled",
                defaultMessage: "Temperature controlled",
              })}
              name="packing.global_configuration.temperature_control_required"
              onChange={(e: any) => updatePackingListProperties(e.target.checked, "temperature_control_required")}
              showCheckbox={packingListSettings?.temperature_controlled_enabled}
            />
            {temperatureControlRequired && (
              <Dropdown
                useHookForm
                required={getValues(`packing.global_configuration.temperature_control_required`)}
                name="packing.global_configuration.temperature_control_value"
                className="schedule-dropdown"
                options={appendDefaultOption(temperatureControlledOptions(intl), intl)}
                defaultValue=""
                onChange={(option: any) => updatePackingListAdditionalProperty("temperature_control_value", option.value)}
                shouldUnregister
              />
            )}
            {temperatureControlValue === "6" && (
              <div className="mt-2">
                <InputField
                  placeholder={intl.formatMessage({
                    id: "new-request.packing-list.pleaseProvide",
                    defaultMessage: "Please provide",
                  })}
                  field_id="packing.global_configuration.temperature_controlled_custom_value"
                  required
                />
              </div>
            )}
          </div>
          {/* Dangerous goods checkbox */}
          <div className="d-flex flex-column">
            <CheckboxWithLabel
              label={intl.formatMessage({
                id: "new-request.packing-list.dangerousGoods",
                defaultMessage: "Dangerous goods",
              })}
              name="packing.global_configuration.is_dangerous_good"
              onChange={(e: any) => {
                if (!e.target.checked) {
                  setValue("packing.global_configuration.dangerous_good_value", "");
                  setValue("packing.global_configuration.un_number", "");
                }
                updatePackingListProperties(e.target.checked, "is_dangerous_good");
              }}
              showCheckbox={packingListSettings?.dangerous_goods_enabled}
            />
            {/* UN Number: same location gate as dangerous goods (packingListSettings.dangerous_goods_enabled). */}
            {packingListSettings?.dangerous_goods_enabled && isDangerousGood && (
              <>
                <Dropdown
                  useHookForm
                  name="packing.global_configuration.dangerous_good_value"
                  className="schedule-dropdown"
                  options={appendDefaultOption(dangerousGoodsOptions(intl), intl)}
                  defaultValue=""
                  onChange={(option: any) => updatePackingListAdditionalProperty("dangerous_good_value", option.value)}
                  required={getValues(`packing.global_configuration.is_dangerous_good`)}
                />
                <div className="mt-2">
                  <InputField
                    label={intl.formatMessage({
                      id: "new-request.packing-list.unNumber",
                      defaultMessage: "UN Number",
                    })}
                    field_id="packing.global_configuration.un_number"
                    placeholder="e.g. UN1203"
                    validations={{
                      maxLength: { value: 20, message: "Maximum 20 characters" },
                      onChange: (e: any) => updatePackingListAdditionalProperty("un_number", e.target.value),
                    }}
                  />
                </div>
              </>
            )}
          </div>
          {/* Handling required checkbox (Only in case of multidrop quote requests) */}
          <div className="d-flex flex-column">
            <CheckboxWithLabel
              label={intl.formatMessage({
                id: "new-request.packing-list.handlingRequired",
                defaultMessage: "Handling required",
              })}
              name="packing.global_configuration.handling_required"
              onChange={(e: any) => updatePackingListProperties(e.target.checked, "handling_required")}
              showCheckbox={packingListSettings?.handling_required_enabled && isMultiDropQuoteRequest}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PackingListGlobalConfiguration;
